import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { getJson } from '@/api/client'
import type { LiveTestConversationDetail, LiveTestTurn } from '@/api/types'
import { Badge } from '@/ui/primitives'

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

function catLabel(cat: string | null): string {
  if (!cat) return ''
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

function formatTs(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatLatency(ms: number | null | undefined): string {
  if (ms == null) return ''
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

interface LiveTestComparisonProps {
  title?: string
  apiBase?: string
  routeBase?: string
}

export function LiveTestComparison({
  title = 'Live Test',
  apiBase = '/v1/admin/live-test',
  routeBase = '/admin/live-test',
}: LiveTestComparisonProps) {
  const { id, turnNum } = useParams<{ id: string; turnNum: string }>()
  const navigate = useNavigate()
  const convId = id ? parseInt(id, 10) : 0
  const startTurn = turnNum ? parseInt(turnNum, 10) : 0

  const [detail, setDetail] = useState<LiveTestConversationDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lightboxImg, setLightboxImg] = useState<string | null>(null)
  const [showContext, setShowContext] = useState(false)

  useEffect(() => { void loadDetail() }, [convId])

  async function loadDetail() {
    try {
      const data = await getJson<LiveTestConversationDetail>(`${apiBase}/conversations/${convId}`)
      setDetail(data)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (!convId) {
    return <div className="flex items-center justify-center min-h-screen text-mute text-sm">Invalid conversation ID</div>
  }

  if (!detail) {
    return <div className="flex items-center justify-center min-h-screen text-mute text-sm">loading…</div>
  }

  const conv = detail.conversation
  const allTurns = detail.turns
  const contextTurns = allTurns.filter((t) => t.sequence < startTurn)
  const compareTurns = allTurns.filter((t) => t.sequence >= startTurn)

  const contextAll: { sender: string; type: string; body: string; ts: string; msg_id?: number }[] = (() => {
    try { return conv.context_json ? JSON.parse(conv.context_json) : [] }
    catch { return [] }
  })()

  function getHumanResponseBlock(turn: LiveTestTurn, idx: number): string | null {
    const nextTurn = compareTurns[idx + 1]
    const after = turn.customer_message_ts
    const before = nextTurn?.customer_message_ts || null
    const msgs = contextAll.filter(m =>
      m.type !== 'customer' &&
      m.ts && after &&
      new Date(m.ts) > new Date(after) &&
      (!before || new Date(m.ts) < new Date(before))
    )
    if (msgs.length === 0) return turn.human_agent_response || null
    return msgs.map(m => {
      const time = m.ts ? formatTs(m.ts) : '—'
      return `[${time}] ${m.sender}: ${m.body}`
    }).join('\n\n')
  }

  return (
    <div className="px-8 lg:px-14 py-12 h-full overflow-y-auto">
      <div className="mb-8">
        <button onClick={() => navigate(`${routeBase}/${convId}`)} className="text-xs text-mute hover:text-cream transition-colors mb-3 block">
          ← Back to Conversation
        </button>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-display text-3xl font-medium text-cream tracking-tight">
            {conv.customer_name || `User #${conv.remote_from_id}`}
          </h1>
          {conv.primary_category && (
            <Badge variant={catBadge(conv.primary_category)}>{catLabel(conv.primary_category)}</Badge>
          )}
        </div>
        <p className="text-sm text-mute mt-1">
          {title} · Comparing turns {startTurn}–{Math.max(startTurn, allTurns.length > 0 ? allTurns[allTurns.length - 1].sequence : startTurn)} · {contextTurns.length} context turns
        </p>
      </div>

      {error && (
        <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5">
          {error}
        </div>
      )}

      {contextTurns.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowContext(!showContext)}
            className="text-xs text-mute hover:text-cream transition-colors mb-3 flex items-center gap-1.5"
          >
            <span>{showContext ? '▼' : '▶'}</span>
            <span>Context ({contextTurns.length} turns)</span>
          </button>
          {showContext && (
            <div className="border border-haze/20 bg-ink/10 overflow-hidden opacity-60">
              <div className="divide-y divide-haze/10">
                {contextTurns.map((turn) => (
                  <div key={turn.id} className="px-3 py-2 flex gap-3 text-[10px]">
                    <span className="text-mute/40 w-8 flex-shrink-0">{turn.sequence}</span>
                    <span className="text-cream/50 whitespace-pre-wrap break-words flex-1">
                      {turn.customer_message || (turn.human_agent_response ? `[Agent] ${turn.human_agent_response}` : '—')}
                    </span>
                    <span className="text-mute/30 flex-shrink-0">{formatTs(turn.customer_message_ts)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {compareTurns.length === 0 ? (
        <div className="text-center text-mute text-xs py-8">No turns to compare.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-haze/40">
                <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[28%]">Customer</th>
                <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[28%]">Human Agent</th>
                <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[44%]">AI Agent</th>
              </tr>
            </thead>
            <tbody>
              {compareTurns.map((turn, rIdx) => {
                const turnAtt = (() => {
                  try { return turn.attachment_json ? JSON.parse(turn.attachment_json) : null }
                  catch { return null }
                })()
                const imgPaths: string[] = (() => {
                  if (!turnAtt) return []
                  if (turnAtt.type === 'images' && Array.isArray(turnAtt.items)) {
                    return turnAtt.items.filter((i: { local_path?: string }) => i.local_path)
                      .map((_: { local_path?: string }, idx: number) => {
                        const suffix = idx > 0 ? `_${idx}` : ''
                        return `${apiBase}/images/${convId}/${turn.remote_msg_id}${suffix}`
                      })
                  }
                  if (turnAtt.local_path) return [`${apiBase}/images/${convId}/${turn.remote_msg_id}`]
                  return []
                })()
                const isAttachmentOnly = !turn.customer_message

                let toolCalls: { id: string; name: string; input: Record<string, unknown> }[] = []
                try {
                  if (turn.tool_calls_json) toolCalls = JSON.parse(turn.tool_calls_json)
                } catch {}

                return (
                  <tr key={turn.id} className={`align-top ${rIdx % 2 === 0 ? 'bg-ink/15' : ''}`}>
                    <td className="px-3 py-2.5 border-r border-haze/15">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] text-mute/50 font-mono">{formatTs(turn.customer_message_ts)}</span>
                        {turn.category && <Badge variant={catBadge(turn.category)}>{catLabel(turn.category)}</Badge>}
                      </div>
                      <div className="bg-ink-raised border border-haze/40 px-3 py-2">
                        {imgPaths.length > 0 ? (
                          <div className={`flex flex-wrap gap-1.5 ${isAttachmentOnly ? '' : 'mb-1.5'}`}>
                            {imgPaths.map((path, idx) => (
                              <img
                                key={idx}
                                src={path}
                                alt={`Attachment ${idx + 1}`}
                                className="max-w-[45%] max-h-32 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => setLightboxImg(path)}
                              />
                            ))}
                          </div>
                        ) : turn.customer_message ? null : (
                          <div className="text-xs italic text-cream/30">[Image]</div>
                        )}
                        {turn.customer_message ? (
                          <div className="text-xs text-cream whitespace-pre-wrap leading-relaxed break-words">
                            {turn.customer_message}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 border-r border-haze/15">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-medium text-ember/70">{turn.human_agent_name || 'Agent'}</span>
                      </div>
                      {getHumanResponseBlock(turn, rIdx) ? (
                        <div className="bg-ember/[0.04] border border-ember/8 px-3 py-2">
                          <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed break-words">
                            {getHumanResponseBlock(turn, rIdx)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[10px] text-mute/30 italic py-2">No human response</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {turn.status === 'pending' ? (
                        <div className="text-[10px] text-mute/30 italic py-2 text-center">Not run yet</div>
                      ) : turn.status === 'failed' ? (
                        <div className="text-[10px] text-ember/50 italic py-2">Failed: {turn.error_message || 'unknown'}</div>
                      ) : turn.ai_response ? (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[9px] font-medium text-emerald/60 uppercase tracking-wider">AI</span>
                            {turn.cost_usd != null && (
                              <span className="text-[9px] text-emerald/60">${turn.cost_usd.toFixed(4)}</span>
                            )}
                            {turn.latency_ms != null && (
                              <span className="text-[9px] text-mute/40">{formatLatency(turn.latency_ms)}</span>
                            )}
                          </div>
                          <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed">
                            {turn.ai_response_plain || turn.ai_response}
                          </div>
                          {toolCalls.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {toolCalls.map((tc, i) => (
                                <span key={i} className="inline-flex items-center px-1 py-px text-[9px] bg-ink border border-emerald/15 text-emerald/50">
                                  {tc.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-[10px] text-mute/30 italic py-2 text-center">—</div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center" onClick={() => setLightboxImg(null)}>
          <button className="absolute top-4 right-4 text-cream text-2xl hover:text-ember transition-colors" onClick={() => setLightboxImg(null)}>✕</button>
          <img src={lightboxImg} alt="Attachment" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  )
}

export function RealChatReplayComparison() {
  return (
    <LiveTestComparison
      title="Real Chat Replay"
      apiBase="/v1/admin/real-chat-replay"
      routeBase="/admin/real-chat-replay"
    />
  )
}
